import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { flushSync } from 'react-dom';
import { ChevronLeft, Search } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import {
  SEARCH_TEXTS,
  type SearchGroup,
  type SearchGroupResult,
  type SearchHit,
} from '@till-erp/platform/lists';
import { newMenu, type NewMenuItem } from '../navigation.ts';
import { apiFetch, ApiProblem } from '../lib/api.ts';
import { formatDate } from '../lib/format.ts';
import { useScreen } from '../lib/layout.ts';
import { hasOpenLayer, useModalFocus } from '../lib/modal-focus.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { isSettingsSearchContext, localSettingsHits, settingsJumpHref, settingsSearchSections, useSettingGroups } from '../lib/settings-search.ts';
import type { SettingsSearchHit } from '../settings-pages.ts';

/**
 * Globale Suche (Strg K, Story 18.13; DESIGN „Suchpille“, EXPERIENCE §2.4, §4.1, §6.2, §10.2): eine Suchpille im Kopf
 * jeder Seite (Desktop und Tablet), am Handy die Lupe in der Kopfzeile als Vollbild. Findet alles gruppiert – Kunden,
 * Aufträge, Belege (nur Nummer), Artikel – über `POST /api/v1/search`, darunter die Befehle aus „+ Neu“. Combobox mit
 * Trefferliste: ↑/↓ markiert, Enter öffnet den markierten (sonst den ersten) Treffer, Esc schließt die Liste. Strg K setzt
 * den Fokus in die Pille; auf Seiten ohne Kopf (oder über einem offenen Panel) öffnet es die Suche als schwebendes Feld.
 */

/** Antwort von `POST /api/v1/search`. */
export interface SearchResponse {
  status: 'ok' | 'too_short';
  /** Mindestlänge eines Begriffs (Einstellung) für „Mindestens … Zeichen eingeben.“ */
  minTermLength: number;
  groups: SearchGroupResult[];
}

/** Wartezeit nach dem letzten Tastendruck, bevor gesucht wird. */
const TYPE_DELAY_MS = 200;

/** Beschriftung eines Befehls aus „+ Neu“ („Auftrag“ → „Neuer Auftrag“). */
const COMMAND_LABELS: Readonly<Record<string, string>> = {
  Auftrag: 'Neuer Auftrag',
  'Auftrag aus PDF': 'Neuer Auftrag aus PDF',
  Rahmenauftrag: 'Neuer Rahmenauftrag',
  Angebot: 'Neues Angebot',
  'Angebot aus Anfrage': 'Neues Angebot aus Anfrage',
  Kunde: 'Neuer Kunde',
  Artikel: 'Neuer Artikel',
  Retoure: 'Neue Retoure',
  Bestellung: 'Neue Bestellung',
  Aufgabe: 'Neue Aufgabe',
};

/**
 * Zweiter Name eines Befehls, nur als Anzeige rechts in der Befehlszeile (Ticket 1.272, S6): neben „Neuer Auftrag“ steht
 * „Auftrag anlegen“ – so heißt der Weg auch in den Abnahme-Szenarien und auf der leeren Auftragsliste. Die Suche filtert
 * nicht danach; die Befehle stehen ohnehin immer in der Liste.
 */
const COMMAND_ALIASES: Readonly<Record<string, string>> = {
  Auftrag: 'Auftrag anlegen',
  Angebot: 'Angebot anlegen',
  Kunde: 'Kunde anlegen',
  Artikel: 'Artikel anlegen',
};

export function commandAlias(item: NewMenuItem): string | null {
  return COMMAND_ALIASES[item.label] ?? null;
}

export function commandLabel(item: NewMenuItem): string {
  return COMMAND_LABELS[item.label] ?? `Neu: ${item.label}`;
}

// ---------------------------------------------------------------------------
// Pillen und schwebende Suche (ein Zustand für die ganze App)

/** Eingabefelder der Suchpillen auf der Seite (Strg K setzt den Fokus in die sichtbare). */
const pills = new Set<HTMLInputElement>();

interface FloatingState {
  open: boolean;
  /** Element, auf das der Fokus nach dem Schließen zurückkehrt. */
  opener: HTMLElement | null;
}

let floating: FloatingState = { open: false, opener: null };
const floatingListeners = new Set<() => void>();
/** Eingabefeld der offenen schwebenden Suche (Strg K setzt den Fokus erneut hinein). */
let floatingInput: HTMLInputElement | null = null;

function setFloating(next: FloatingState) {
  floating = next;
  for (const l of floatingListeners) l();
}

/** Suche als schwebendes Feld (Desktop, Tablet) bzw. Vollbild (Handy) öffnen. */
export function openGlobalSearch(opener: HTMLElement | null = null): void {
  if (floating.open) {
    floatingInput?.focus();
    return;
  }
  setFloating({ open: true, opener });
}

function closeGlobalSearch(): void {
  setFloating({ open: false, opener: null });
}

function useFloating(): FloatingState {
  return useSyncExternalStore(
    (l) => {
      floatingListeners.add(l);
      return () => floatingListeners.delete(l);
    },
    () => floating,
    () => floating,
  );
}

function visible(el: HTMLElement): boolean {
  return el.isConnected && el.getClientRects().length > 0 && !el.closest('[inert], [aria-hidden="true"]');
}

/**
 * Wie Strg K (auch für den Knopf „Suchen“ in der Seitenleiste): Fokus in die sichtbare Suchpille; gibt es keine oder liegt
 * eine Überlagerung darüber, öffnet sich die schwebende Suche bzw. am Handy das Vollbild.
 */
export function focusGlobalSearch(opener: HTMLElement | null): void {
  if (floating.open) {
    floatingInput?.focus();
    floatingInput?.select();
    return;
  }
  if (!hasOpenLayer()) {
    const pill = [...pills].find(visible);
    if (pill) {
      pill.focus();
      pill.select();
      return;
    }
  }
  openGlobalSearch(opener);
}

/**
 * Strg K überall (EXPERIENCE §6.2): Fokus in die Suchpille; gibt es keine (Seite ohne Kopf, Handy) oder liegt eine
 * Überlagerung darüber, öffnet sich die Suche als schwebendes Feld bzw. am Handy als Vollbild. „⌘K“ am Mac ebenso.
 */
export function useSearchShortcut(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'k') return;
      // Auch im Browser belegt (Suchleiste): nie dorthin.
      e.preventDefault();
      focusGlobalSearch(document.activeElement instanceof HTMLElement ? document.activeElement : null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}

// ---------------------------------------------------------------------------
// Abfrage

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

type SearchView =
  | { state: 'empty' }
  | { state: 'too_short'; minTermLength: number }
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'nothing'; q: string }
  | { state: 'hits'; groups: SearchGroupResult[] };

function searchQueryOptions(getToken: ReturnType<typeof useSession>['getToken'], text: string, identity: string) {
  return {
    queryKey: ['global-search', identity, text] as const,
    queryFn: () => apiFetch<SearchResponse>(getToken, '/api/v1/search', { method: 'POST', body: { q: text } }),
    // Treffer enthalten Personendaten: nicht länger als nötig im Speicher.
    gcTime: 0,
    staleTime: 10_000,
    retry: false,
  };
}

interface GlobalSearchState {
  view: SearchView;
  /** Die gezeigten Treffer gehören zum getippten Text (nicht die einer vorigen Eingabe während der Wartezeit). */
  current: boolean;
  /** Antwort zum getippten Text sofort holen (Enter während der Wartezeit); `null` = leer, keine Abfrage. */
  fetchCurrent: () => Promise<SearchResponse | null>;
}

function useGlobalSearch(q: string): GlobalSearchState {
  const { getToken, me } = useSession();
  const identity = `${me.id}:${me.role}`;
  const queryClient = useQueryClient();
  const trimmed = q.trim();
  const debounced = useDebounced(trimmed, TYPE_DELAY_MS);
  // Grenzen (Mindest- und Höchstlänge) kennt nur der Server (Einstellungen): jeder nicht leere Text geht hin.
  const enabled = debounced !== '';
  const result = useQuery({ ...searchQueryOptions(getToken, debounced, identity), enabled, placeholderData: (previousData, previousQuery) => previousQuery?.queryKey[1] === identity ? previousData : undefined });
  // Gehört die Antwort zum getippten Text? Sonst bleibt die vorige Antwort stehen (kein Springen beim Tippen).
  const current = debounced === trimmed && !result.isPlaceholderData && !result.isFetching;
  const fetchCurrent = () => (trimmed !== '' ? queryClient.fetchQuery(searchQueryOptions(getToken, trimmed, identity)) : Promise.resolve(null));
  const state = (view: SearchView): GlobalSearchState => ({ view, current, fetchCurrent });
  if (trimmed === '') return state({ state: 'empty' });
  if (result.isError) {
    const err = result.error;
    return state({ state: 'error', message: err instanceof ApiProblem ? err.detail : 'Die Suche ist gerade nicht möglich. Bitte gleich noch einmal versuchen.' });
  }
  const data = result.data;
  if (!data) return state({ state: 'loading' });
  if (data.status === 'too_short') return state(current ? { state: 'too_short', minTermLength: data.minTermLength } : { state: 'loading' });
  if (data.groups.length === 0) return state(current ? { state: 'nothing', q: trimmed } : { state: 'loading' });
  return state({ state: 'hits', groups: data.groups });
}

// ---------------------------------------------------------------------------
// Trefferliste

const NO_GROUPS: SearchGroupResult[] = [];

interface Option {
  id: string;
  kind: 'hit' | 'command' | 'setting';
  setting?: SettingsSearchHit;
  group?: SearchGroup;
  hit?: SearchHit;
  command?: NewMenuItem;
}

function useOpenHit(): (group: SearchGroup, id: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (group, id) => {
      switch (group) {
        case 'orders':
          void navigate({ to: '/auftraege/$id', params: { id } });
          break;
        case 'customers':
          void navigate({ to: '/kunden/$id', params: { id } });
          break;
        case 'articles':
          void navigate({ to: '/artikel/$id', params: { id } });
          break;
        case 'documents':
          void navigate({ to: '/belege/$documentId', params: { documentId: id } });
          break;
      }
    },
    [navigate],
  );
}

/** Teile des Entwurfs A03 ohne Funktion (Ticket 1.243): erscheinen bei leerem Feld ausgegraut. */
const SEARCH_LATER = ['Zuletzt geöffnet', 'Favoriten'] as const;
const SEARCH_LATER_HINT = 'kommt mit einer späteren Story';

/**
 * Eingabe und Trefferliste (Combobox nach WAI-ARIA): Der Fokus bleibt im Feld, `aria-activedescendant` zeigt den
 * markierten Treffer. `variant`: Pille im Kopf (Liste schwebt darunter), schwebendes Feld oder Vollbild am Handy (Liste
 * immer offen).
 */
function SearchCombobox({
  variant,
  inputRef,
  onDone,
}: {
  variant: 'pill' | 'floating' | 'phone';
  inputRef: RefObject<HTMLInputElement | null>;
  /** Nach dem Öffnen eines Treffers bzw. Befehls (schwebend/Handy: schließen). */
  onDone?: () => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(variant !== 'pill');
  // Eine lokale Markierung bleibt bei nachgeladenen Datensätzen bestehen; neuer Text oder Kontext wählt wieder den ersten.
  const [marked, setMarked] = useState<{ options: readonly Option[] | null; index: number; query?: string; context?: string; id?: string }>({ options: null, index: 0 });
  const { view, current, fetchCurrent } = useGlobalSearch(q);
  const location = useRouterState({ select: (state) => state.location });
  const { me } = useSession();
  const inSettings = isSettingsSearchContext(location.pathname);
  const settingGroups = useSettingGroups(inSettings);
  const sections = useMemo(() => settingsSearchSections(settingGroups.data?.groups, me), [settingGroups.data, me]);
  const settings = useMemo(() => localSettingsHits(location.pathname, sections, q), [location.pathname, sections, q]);
  // Text und Route werden sofort aktualisiert: verspätete Antworten dürfen keine alte Navigation ausführen.
  const context = `${me.id}:${me.role}:${location.pathname}:${location.searchStr}`;
  const latest = useRef({ q, context, settings });
  const pendingEnter = useRef(0);
  useLayoutEffect(() => {
    latest.current = { q, context, settings };
  }, [q, context, settings]);
  useLayoutEffect(() => {
    pendingEnter.current += 1;
  }, [q, context]);
  const [previousContext, setPreviousContext] = useState(context);
  if (previousContext !== context) {
    setPreviousContext(context);
    setOpen(variant !== 'pill');
    setMarked({ options: null, index: 0 });
  }
  const openHit = useOpenHit();
  const navigate = useNavigate();
  const baseId = useId();
  const listId = `${baseId}-liste`;
  const messageId = `${baseId}-hinweis`;
  const wrapper = useRef<HTMLDivElement>(null);
  const always = variant !== 'pill';
  const expanded = always || open;

  const groups = view.state === 'hits' ? view.groups : NO_GROUPS;
  const options = useMemo<Option[]>(() => {
    const out: Option[] = settings.map((setting) => ({ id: `${baseId}-einstellung-${encodeURIComponent(setting.id)}`, kind: 'setting', setting }));
    for (const g of groups) for (const hit of g.hits) out.push({ id: `${baseId}-${g.group}-${hit.id}`, kind: 'hit', group: g.group, hit });
    newMenu.forEach((command, i) => {
      if (command.path) out.push({ id: `${baseId}-befehl-${i}`, kind: 'command', command });
    });
    return out;
  }, [settings, groups, baseId]);

  const remembered = marked.query === q && marked.context === context ? options.findIndex((option) => option.id === marked.id) : -1;
  const active = marked.options === options ? marked.index : remembered >= 0 ? remembered : 0;

  const activeOption = expanded ? options[Math.min(active, options.length - 1)] : undefined;

  const pick = (option: Option | undefined) => {
    if (!option) return;
    pendingEnter.current += 1;
    if (option.kind === 'setting' && option.setting) {
      // Die Fokusfalle samt Rückkehr zum Suchöffner vor dem Feldsprung abbauen.
      if (onDone) flushSync(onDone);
      void navigate({ href: settingsJumpHref(option.setting.href, location.pathname, location.searchStr) });
    } else if (option.kind === 'hit' && option.group && option.hit) openHit(option.group, option.hit.id);
    else if (option.command?.path) void navigate({ to: option.command.path, search: option.command.search });
    else return;
    setQ('');
    setOpen(false);
    if (variant === 'pill') inputRef.current?.blur();
    if (option.kind !== 'setting') onDone?.();
  };

  // Vorgemerktes Enter (Wartezeit): jede Schließung der Liste bzw. Ebene verwirft es, damit eine verspätete Antwort
  // nach Esc oder Klick daneben nicht noch die Seite wechselt.
  useEffect(() => {
    if (!expanded) pendingEnter.current += 1;
  }, [expanded]);
  useEffect(
    () => () => {
      pendingEnter.current += 1;
    },
    [],
  );

  const openFirstWhenCurrent = () => {
    const asked = q;
    const askedContext = context;
    const token = ++pendingEnter.current;
    void fetchCurrent()
      .then((res) => {
        if (pendingEnter.current !== token || latest.current.q !== asked || latest.current.context !== askedContext) return;
        const setting = latest.current.settings[0];
        if (setting) {
          pick({ id: '', kind: 'setting', setting });
          return;
        }
        if (!res || res.status !== 'ok') return;
        const first = res.groups[0];
        const hit = first?.hits[0];
        if (first && hit) pick({ id: '', kind: 'hit', group: first.group, hit });
      })
      .catch(() => {
        // Fehler zeigt die Liste selbst (gleiche Abfrage).
      });
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!expanded) {
        setOpen(true);
        setMarked({ options: null, index: 0 });
        return;
      }
      if (options.length === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const index = (Math.min(active, options.length - 1) + step + options.length) % options.length;
      setMarked({ options, index, query: q, context, id: options[index]?.id });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      // Gezeigt werden evtl. noch die Treffer der vorigen Eingabe (Wartezeit): nie einen davon öffnen, sondern den ersten
      // Treffer der aktuellen Antwort, sobald sie da ist (zu kurz oder nichts gefunden: nichts).
      if (activeOption?.kind === 'setting') pick(activeOption);
      else if (inSettings && settings.length === 0 && q.trim() !== '' && view.state === 'nothing' && marked.options !== options) return;
      else if (current || view.state === 'empty') pick(activeOption);
      else openFirstWhenCurrent();
      return;
    }
    if (e.key === 'Escape') {
      pendingEnter.current += 1;
      // Pille: Esc schließt die Liste (zweites Esc leert das Feld). Schwebend/Handy: schließt die Ebene (useModalFocus).
      if (variant !== 'pill') return;
      e.preventDefault();
      if (open) setOpen(false);
      else setQ('');
    }
  };

  // Pille: Klick daneben schließt die Liste.
  useEffect(() => {
    if (variant !== 'pill' || !open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) {
        pendingEnter.current += 1;
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [variant, open]);

  // Pille anmelden (Strg K).
  useEffect(() => {
    const el = inputRef.current;
    if (variant !== 'pill' || !el) return;
    pills.add(el);
    return () => {
      pills.delete(el);
    };
  }, [variant, inputRef]);

  const message =
    view.state === 'too_short'
      ? `${settings.length > 0 ? 'Datensatzsuche: ' : ''}${SEARCH_TEXTS.tooShort(view.minTermLength)}`
      : view.state === 'nothing' && settings.length === 0
        ? SEARCH_TEXTS.nothingFound(view.q)
        : view.state === 'loading'
          ? 'Suche …'
          : view.state === 'error'
            ? view.message
            : null;

  const input = (
    <input
      ref={inputRef}
      type="search"
      role="combobox"
      aria-label="Suche"
      aria-expanded={expanded}
      aria-controls={listId}
      aria-autocomplete="list"
      aria-activedescendant={activeOption?.id}
      aria-invalid={view.state === 'error' ? true : undefined}
      aria-describedby={message ? messageId : undefined}
      autoComplete="off"
      spellCheck={false}
      enterKeyHint="search"
      placeholder={SEARCH_TEXTS.placeholder}
      value={q}
      onChange={(e) => {
        setQ(e.target.value);
        setOpen(true);
      }}
      onFocus={() => setOpen(true)}
      // Pille: Fokus verlässt die Suche (z. B. Tab) → Liste zu. Treffer nehmen nie den Fokus (Combobox).
      onBlur={(e) => {
        if (variant === 'pill' && !wrapper.current?.contains(e.relatedTarget as Node | null)) {
          pendingEnter.current += 1;
          setOpen(false);
        }
      }}
      onKeyDown={onKeyDown}
      // Fokusring an der Pille bzw. am Feldrahmen, nicht am Eingabefeld darin (sonst zwei Ringe; die globale Regel
      // `:focus-visible` in styles.css liegt außerhalb der Tailwind-Ebenen).
      style={{ outline: 'none' }}
      className={cn(
        'h-full min-w-0 flex-1 bg-transparent text-ink placeholder:text-ink-3 [&::-webkit-search-cancel-button]:hidden',
        variant === 'phone' ? 'text-field-phone' : 'text-row',
      )}
    />
  );

  const settingsAnnouncement = inSettings ? <p role="status" aria-live="polite" className="sr-only" data-testid="einstellungs-suche-ansage">
    {expanded && q.trim() !== '' && sections ? (settings.length === 0 ? 'Keine Treffer.' : settings.length === 1 ? '1 Treffer' : `${settings.length} Treffer`) : ''}
  </p> : null;

  const list = (
    <div
      id={listId}
      role="listbox"
      aria-label="Treffer und Befehle"
      // Treffer nehmen nie den Fokus (Combobox): Bei vielen Treffern ist die Liste scrollbar, und Chrome würde sie sonst
      // per Tab fokussieren – die Liste bliebe nach Tab offen.
      tabIndex={-1}
      className={cn(
        variant === 'pill' &&
          'absolute top-[calc(100%+8px)] left-0 z-40 max-h-[min(70vh,560px)] w-[max(100%,min(440px,calc(100vw-32px)))] overflow-y-auto rounded-card bg-card p-2 shadow-float',
        variant === 'floating' && 'mt-2 max-h-[min(60vh,560px)] overflow-y-auto rounded-card bg-card p-2 shadow-float',
        variant === 'phone' && 'mt-3 min-h-0 flex-1 overflow-y-auto pb-6',
      )}
      hidden={!expanded}
    >
      {message ? (
        <p
          id={messageId}
          role={view.state === 'error' ? 'alert' : 'status'}
          className={cn('type-meta px-3 py-2', view.state === 'error' ? 'text-clarify' : 'text-ink-2')}
        >
          {message}
        </p>
      ) : (
        <span id={messageId} role="status" className="sr-only" />
      )}
      <ul role="presentation" className="flex list-none flex-col p-0">
        {inSettings ? (
          <li role="presentation">
            <ul role="group" aria-labelledby={`${baseId}-einstellungen-kopf`} className="flex list-none flex-col p-0">
              <li role="presentation" id={`${baseId}-einstellungen-kopf`} className="type-meta px-3 pt-2 pb-1 text-ink-3">Einstellungen</li>
              {settings.map((hit) => {
                const id = `${baseId}-einstellung-${encodeURIComponent(hit.id)}`;
                const head = hit.trail.slice(0, -1);
                return (
                  <SearchOption key={hit.id} id={id} selected={activeOption?.id === id} kind={hit.kind} onHover={() => setMarked({ options, index: options.findIndex((option) => option.id === id), query: q, context, id })} onPick={() => pick(options.find((option) => option.id === id))}>
                    <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                      {head.length > 0 ? <span className="text-ink-3">{head.join(' › ')} › </span> : null}
                      <span className="font-semibold text-ink">{hit.trail.at(-1)}</span>
                    </span>
                  </SearchOption>
                );
              })}
              {settingGroups.error ? <li role="presentation"><p role="alert" className="type-meta px-3 py-2 text-clarify">{settingGroups.error.message}</p></li> : null}
              {!sections && !settingGroups.error ? <li role="presentation" className="type-meta px-3 py-2 text-ink-2">Einstellungen laden …</li> : null}
              {sections && q.trim() !== '' && settings.length === 0 ? <li role="presentation" className="type-meta px-3 py-2 text-ink-3">Keine Treffer.</li> : null}
            </ul>
          </li>
        ) : null}
        {groups.map((g) => (
          <li key={g.group} role="presentation">
            <ul role="group" aria-labelledby={`${baseId}-${g.group}-kopf`} className="flex list-none flex-col p-0">
              <li role="presentation" id={`${baseId}-${g.group}-kopf`} className="type-meta px-3 pt-2 pb-1 text-ink-3">
                {g.label}
              </li>
              {g.hits.map((hit) => {
                const id = `${baseId}-${g.group}-${hit.id}`;
                return (
                  <SearchOption key={hit.id} id={id} selected={activeOption?.id === id} stale={!current} onPick={() => pick(options.find((o) => o.id === id))}>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold [overflow-wrap:anywhere] text-ink">{hit.title}</span>
                      {hit.subtitle || hit.onDate ? (
                        <span className="type-meta block truncate text-ink-2" title={hit.subtitle ?? undefined}>
                          {[hit.subtitle, hit.onDate ? formatDate(hit.onDate) : null].filter(Boolean).join(' · ')}
                        </span>
                      ) : null}
                    </span>
                    {hit.probe ? <span className="type-meta flex-none rounded-full bg-tile px-2 text-ink-2">{SEARCH_TEXTS.probe}</span> : null}
                  </SearchOption>
                );
              })}
              {g.more ? (
                <li role="presentation" className="type-meta px-3 pb-1 text-ink-3">
                  {SEARCH_TEXTS.more}
                </li>
              ) : null}
            </ul>
          </li>
        ))}
        {view.state === 'empty' && !inSettings ? (
          // Entwurf A03 (Ticket 1.243): „Zuletzt geöffnet“ und „Favoriten“ gibt es noch nicht – ausgegraut mit Hinweis
          // (Rudolf 17.10.2026), keine Optionen der Liste.
          <li role="presentation" data-testid="suche-spaeter" className="flex flex-col gap-space-1 px-3 pt-2 pb-1">
            {SEARCH_LATER.map((label) => (
              <p key={label} aria-disabled="true" className="flex items-center justify-between gap-3 text-row text-ink-3">
                {label}
                <span className="type-meta">{SEARCH_LATER_HINT}</span>
              </p>
            ))}
          </li>
        ) : null}
        <li role="presentation">
          <ul role="group" aria-labelledby={`${baseId}-befehle-kopf`} className="flex list-none flex-col p-0">
            <li role="presentation" id={`${baseId}-befehle-kopf`} className="type-meta px-3 pt-2 pb-1 text-ink-3">
              {SEARCH_TEXTS.commands}
            </li>
            {newMenu.map((command, i) => {
              const id = `${baseId}-befehl-${i}`;
              return command.path ? (
                <SearchOption key={command.label} id={id} selected={activeOption?.id === id} onPick={() => pick(options.find((o) => o.id === id))}>
                  <span className="min-w-0 flex-1 text-ink">{commandLabel(command)}</span>
                  {commandAlias(command) ? <span className="type-meta flex-none text-ink-3">{commandAlias(command)}</span> : null}
                </SearchOption>
              ) : (
                <li
                  key={command.label}
                  id={id}
                  role="option"
                  aria-selected={false}
                  aria-disabled="true"
                  className="flex min-h-10 items-center justify-between gap-3 rounded-field px-3 text-row text-ink-3 pointer-coarse:min-h-11"
                >
                  {commandLabel(command)}
                  <span className="type-meta">kommt bald</span>
                </li>
              );
            })}
          </ul>
        </li>
      </ul>
    </div>
  );

  if (variant === 'phone') {
    return (
      <>
        <label className="flex h-11 items-center gap-2 rounded-field border border-line bg-card px-3 has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-ring">
          <Search size={17} strokeWidth={1.5} aria-hidden className="flex-none text-ink-3" />
          {input}
        </label>
        {settingsAnnouncement}
        {list}
      </>
    );
  }

  return (
    <div ref={wrapper} className={cn('relative', variant === 'pill' ? 'w-full' : '')}>
      {/* Ganze Pille ist das Feld (Klick auf Lupe oder Kürzel setzt den Fokus); Fokusring nur bei Tastatur. */}
      <label
        className={cn(
          'flex cursor-text items-center gap-2 rounded-full pr-2 pl-4 has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-ring',
          // Design S3: Kopfzeilen-Pille weiß durchscheinend mit feinem Rand (als Innenschatten, damit das Feld volle 44 px
          // Trefferfläche behält); schwebende Suche bleibt Glas.
          // Redesign OpenAI-Stil (31.10.2026): ruhiges graues Feld ohne Rand, höchstens 560 px breit.
          variant === 'pill'
            ? 'h-10 max-w-[560px] bg-surface-card pointer-coarse:h-11 transition-colors duration-150 hover:bg-hover'
            : 'h-12 bg-[var(--glass)] shadow-float backdrop-blur-[16px]',
        )}
      >
        <Search size={17} strokeWidth={1.5} aria-hidden className="flex-none text-ink-3" />
        {input}
        <kbd aria-hidden className={cn('type-meta flex-none rounded-mark bg-card px-2 py-0.5 font-sans text-ink-3', variant === 'pill' && 'max-lg:hidden')}>
          Strg K
        </kbd>
      </label>
      {settingsAnnouncement}
      {list}
    </div>
  );
}

/** Treffer; `stale` = er gehört noch zur vorigen Eingabe (Wartezeit): grau, nicht wählbar. */
function SearchOption({ id, selected, stale = false, kind, onHover, onPick, children }: { id: string; selected: boolean; stale?: boolean; kind?: SettingsSearchHit['kind']; onHover?: () => void; onPick: () => void; children: ReactNode }) {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [selected]);
  return (
    <li
      ref={ref}
      id={id}
      role="option"
      data-kind={kind}
      aria-selected={selected && !stale}
      aria-disabled={stale ? 'true' : undefined}
      // Fokus bleibt im Feld (Combobox); Klick bzw. Tippen öffnet – nie einen Treffer der vorigen Eingabe.
      // Markieren nur bei echter Mausbewegung: verrutscht die Liste unter der ruhenden Maus (z. B. weil
      // „Suche …“ verschwindet), feuert der Browser mouseenter, aber kein mousemove.
      onMouseMove={selected ? undefined : onHover}
      onMouseDown={(e) => e.preventDefault()}
      onClick={stale ? undefined : onPick}
      className={cn(
        'flex min-h-11 items-center gap-3 rounded-field px-3 py-1.5 text-row',
        stale ? 'cursor-default opacity-50' : 'cursor-pointer hover:bg-tile',
        selected && !stale && 'bg-tile shadow-marker',
      )}
    >
      {children}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Bausteine für die App-Hülle

/** Suchpille links in der Kopfzeile (Design S3, Desktop und Tablet): nimmt die freie Breite neben Anlegen und Glocke. */
export function SearchPill() {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div role="search" aria-label="Globale Suche" className="min-w-0 flex-1">
      <SearchCombobox variant="pill" inputRef={inputRef} />
    </div>
  );
}

/**
 * Schwebende Suche bzw. Vollbild am Handy (EXPERIENCE §10.2), geöffnet mit Strg K (Seite ohne Kopf, über einem Panel)
 * oder der Lupe in der Kopfzeile. Eigene Ebene mit Fokusfalle; Esc oder „Zurück“ schließt, der Fokus kehrt zurück.
 */
export function GlobalSearchHost() {
  const state = useFloating();
  const phone = useScreen() === 'phone';
  if (!state.open) return null;
  return <FloatingSearch phone={phone} opener={state.opener} />;
}

function FloatingSearch({ phone, opener }: { phone: boolean; opener: HTMLElement | null }) {
  const panel = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLElement | null>(opener);
  useModalFocus(panel, openerRef, closeGlobalSearch, () => inputRef.current);
  useEffect(() => {
    floatingInput = inputRef.current;
    return () => {
      floatingInput = null;
    };
  }, []);
  if (phone) {
    return (
      <div ref={panel} role="dialog" aria-modal="true" aria-label="Suche" className="fixed inset-0 z-[60] flex flex-col overflow-x-hidden bg-card px-4 pt-4">
        <div className="flex items-center gap-2 pb-3">
          <button
            type="button"
            onClick={closeGlobalSearch}
            className="inline-flex h-11 cursor-pointer items-center gap-1 rounded-full bg-secondary pr-4 pl-3 text-row text-secondary-foreground"
          >
            <ChevronLeft size={18} strokeWidth={1.5} aria-hidden />
            Zurück
          </button>
        </div>
        <SearchCombobox variant="phone" inputRef={inputRef} onDone={closeGlobalSearch} />
      </div>
    );
  }
  return (
    <>
      <div aria-hidden className="fixed inset-0 z-[55] bg-scrim" onClick={closeGlobalSearch} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Suche"
        className="fixed inset-x-0 z-[60] mx-auto w-[min(560px,calc(100vw-32px))]"
        style={{ top: 'calc(var(--banner-h, 0px) + 72px)' }}
      >
        <SearchCombobox variant="floating" inputRef={inputRef} onDone={closeGlobalSearch} />
      </div>
    </>
  );
}
